import React, { useEffect, useState } from "react";
import axios from "axios";
import { Icon } from "organisms/iconCommonent";
import { FaRegEdit } from "react-icons/fa";
import { ApiContent } from "./shaping";

interface Nurturing {
  _id: string;
  heading: string;
  image?: string;
  video: string; // YouTube URL
}

export default function Social() {
  const [banner, setBanner] = useState<Nurturing | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingBanner, setEditingBanner] = useState<Nurturing | null>(null);
  const [apiContent, setApiContent] = useState<ApiContent | null>(null);
  const [showContentForm, setShowContentForm] = useState(false);
  // Fetch banners
  const fetchBanners = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_LOCAL_URL}/api/social-changes`);

      const content = await axios.get(
        `${process.env.REACT_APP_LOCAL_URL}/api/content/68efd1662d25c3fab3031cb3`
      );



      if (content.data.status === "success") {
        setApiContent(content.data.data);
      }

      if (res.data.status === "success") {
        setBanner(res.data.message[2]);
      }
    } catch (error) {
      console.error("Error fetching banner:", error);
    }
  };

  useEffect(() => {
    fetchBanners();
  }, []);

  const handleEdit = (banner: Nurturing) => {
    setEditingBanner(banner);
    setShowForm(true);
  };

  return (
    <div className="pt-8">
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-lato-regular">Section -10</h2>
        <hr />
      </div>

      <div className="md:flex items-center justify-between border-b border-[#84848480] border-opacity-50">
        <h4 className="pb-3 font-lato-bold text-[#07393C]">
          {apiContent?.heading}
        </h4>
        <button
          className="bg-blue-500 text-white px-4 py-2 rounded"
          onClick={() => setShowContentForm(true)} // ✅ Open section edit modal
        >
          <Icon icon={FaRegEdit} />
        </button>
      </div>

      {banner && (
        <div className="w-full bg-white border border-gray-200 rounded-xl overflow-hidden">
          {banner.image && (
            <img
              src={`${process.env.REACT_APP_LOCAL_URL}/${banner.image}`}
              alt={banner.heading}
              className="w-full h-[20rem] object-cover object-top"
            />
          )}

          {/* Video Display */}

          {banner.video ? (
            <div className="mx-2 mt-4 w-[35rem] h-[15rem]">
              <iframe
                width="100%"
                height="100%"
                src={banner.video}
                title="YouTube video player"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              ></iframe>
            </div>
          ) : null}

          <div className="p-5">
            <h2
              className="text-xl font-semibold text-black font-lato-regular mb-2"
              dangerouslySetInnerHTML={{ __html: banner.heading }}
            />

            <div className="flex justify-start gap-3">
              <button
                className="bg-blue-500 font-lato-regular text-white px-4 py-2 rounded"
                onClick={() => handleEdit(banner)}
              >
                <span className="inline-block mr-1">
                  <Icon icon={FaRegEdit} />
                </span>
                Edit
              </button>
            </div>
          </div>
        </div>
      )}

      {showForm && editingBanner && (
        <BannerFormModal
          onClose={() => setShowForm(false)}
          fetchBanners={fetchBanners}
          editingBanner={editingBanner}
        />
      )}

      {/* Section Content Edit Modal */}
      {showContentForm && apiContent && (
        <SectionContentModal
          onClose={() => setShowContentForm(false)}
          apiContent={apiContent}
          fetchImpact={fetchBanners}
        />
      )}
    </div>
  );
}

// -------------------- Banner Form Modal --------------------

interface BannerFormModalProps {
  onClose: () => void;
  fetchBanners: () => void;
  editingBanner: Nurturing;
}

const BannerFormModal = ({
  onClose,
  fetchBanners,
  editingBanner,
}: BannerFormModalProps) => {
  const [heading, setHeading] = useState(editingBanner.heading || "");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [videoUrl, setVideoUrl] = useState<string>(editingBanner.video || "");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!heading.trim()) return;

    const formData = new FormData();
    formData.append("heading", heading);

    if (imageFile) formData.append("image", imageFile);
    if (videoUrl) {
      formData.append("video", videoUrl); // upload file
    }

    try {
      await axios.put(
        `${process.env.REACT_APP_LOCAL_URL}/api/social-changes/${editingBanner._id}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      fetchBanners();
      onClose();
    } catch (error) {
      console.error("Error updating banner:", error);
      alert("Failed to update banner. Check console for details.");
    }
  };

  return (
    <div className="fixed inset-0 flex justify-center items-center bg-black/40 z-50">
      <div className="bg-white p-6 rounded-xl w-[500px] shadow-lg max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Heading */}
          <label className="block font-lato-bold">Heading</label>
          <input
            type="text"
            value={heading}
            onChange={(e) => setHeading(e.target.value)}
            required
            className="w-full border border-gray-300 rounded-md p-2"
          />

          {/* Image Preview */}
          {editingBanner.image && !imageFile && (
            <img
              src={`${process.env.REACT_APP_LOCAL_URL}/${editingBanner.image}`}
              alt="banner"
              className="w-24 h-24 object-cover rounded-md mb-2"
            />
          )}
          <label className="block font-lato-bold">Upload Image</label>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => setImageFile(e.target.files?.[0] || null)}
            className="w-full border border-gray-300 rounded-md p-2"
          />

          {/* Video Preview */}

          {editingBanner.video ? (
            <iframe
              width="100%"
              height="200px"
              src={editingBanner.video}
              title="YouTube video player"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            ></iframe>
          ) : null}

          {/* Video Inputs */}
          <label className="block font-lato-bold">YouTube Video URL</label>
          <input
            type="text"
            value={videoUrl}
            onChange={(e) => setVideoUrl(e.target.value)}
            placeholder="Enter YouTube URL"
            className="w-full border border-gray-300 rounded-md p-2"
          />

          {/* Buttons */}
          <div className="flex justify-end gap-3 mt-4">
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Update
            </button>
            <button
              type="button"
              onClick={onClose}
              className="text-red-500 border border-red-500 px-4 py-2 rounded-md hover:bg-red-50"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

interface SectionContentModalProps {
  onClose: () => void;
  apiContent: ApiContent;
  fetchImpact: () => void;
}

const SectionContentModal = ({
  onClose,
  apiContent,
  fetchImpact,
}: SectionContentModalProps) => {
  const [heading, setHeading] = useState(apiContent.heading);
  const [description, setDescription] = useState(apiContent.description);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await axios.patch(
        `${process.env.REACT_APP_LOCAL_URL}/api/content/68eccf219b4d8bb484b69e3b`,
        { heading, description }
      );
      fetchImpact();
      onClose();
    } catch (error) {
      console.error("Error updating section:", error);
      alert("Failed to update section content");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex justify-center items-center z-50">
      <div className="bg-white p-6 rounded-xl w-[500px] shadow-lg">
        <h3 className="text-lg font-lato-bold mb-4">Edit Section Content</h3>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="block font-lato-bold">Heading</label>
            <input
              type="text"
              value={heading}
              onChange={(e) => setHeading(e.target.value)}
              className="w-full border border-gray-300 rounded-md p-2"
              required
            />
          </div>

          <div>
            <label className="block font-lato-bold">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full border border-gray-300 rounded-md p-2 h-24"
              required
            />
          </div>

          <div className="flex justify-end gap-3 mt-4">
            <button
              type="submit"
              className="px-4 py-2 bg-teal text-white rounded-md "
            >
              Update
            </button>
            <button
              type="button"
              onClick={onClose}
              className="text-red-500 border border-red-500 px-4 py-2 rounded-md hover:bg-red-50"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
